import { glyphUrl, PLAQUE_SMALL, LABEL } from './hud-theme';
import { t, getIntlLocale } from '../i18n/i18n';
/**
 * "No clear goals or spawn logic" feedback — the exit portcullis was already
 * rendered as a beacon (dungeon-instance-scene.ts), but nothing told the
 * player which direction to walk to reach it. This is a lightweight compass
 * arrow, not a minimap: it points at the nearest portcullis and shows the
 * distance, reusing data the scene already receives. A real minimap/quest
 * tracker is a separate, larger HUD design task.
 */

/**
 * Bearing from the player to a target, relative to camera yaw, in radians.
 * 0 = target is straight ahead on screen; positive = target is to the left,
 * negative = to the right (matches OverShoulderCameraController.worldDirection's
 * convention: x = sin(angle), z = cos(angle), and "right" = forward × up,
 * which at yaw=0 works out to -X — so a target displaced toward +X, i.e. a
 * larger raw angle, sits on the LEFT of the view).
 */
export function computeBearing(playerX: number, playerZ: number, targetX: number, targetZ: number, yaw: number): number {
  const targetAngle = Math.atan2(targetX - playerX, targetZ - playerZ);
  let relative = targetAngle - yaw;
  while (relative > Math.PI) relative -= Math.PI * 2;
  while (relative < -Math.PI) relative += Math.PI * 2;
  return relative;
}

export interface RoutePoint { x: number; z: number }

export interface RouteGuidance {
  /** The point to aim the arrow at — the next waypoint ahead, not the far destination. */
  x: number;
  z: number;
  /** Distance still to travel *along the route*, not as the crow flies. */
  remainingM: number;
  /** Index of the targeted waypoint, exposed for tests/debugging. */
  index: number;
}

/**
 * Warden's Route amendment (2026-08-24) — this compass used to point
 * straight at the nearest portcullis, which was honest enough when the
 * exit was 4-7 rooms from spawn. It isn't any more: the exit now sits
 * behind the Vault, 12-18 rooms out (generator.ts), so a crow-flies
 * bearing regularly points through ten rooms of solid wall and walks the
 * player into dead ends.
 *
 * Instead the arrow follows the route the server already computed for the
 * run-start fly-through (`layout.flythrough.waypoints`: alternating room
 * centres and the gate between each pair, entrance to exit). Pointing at
 * the *next* waypoint past the player's current position turns the same
 * one-arrow HUD into a breadcrumb that actually runs down the corridors —
 * and because that path crosses the Vault by construction, following the
 * arrow walks you into the Bone Warden's room rather than around it.
 *
 * Still not a minimap: one arrow, one distance, no map of anywhere the
 * player hasn't been. Walking off-route points the arrow back at the
 * route, which is the behaviour you want from a breadcrumb.
 */
export function nextRouteWaypoint(route: readonly RoutePoint[], playerX: number, playerZ: number): RouteGuidance | null {
  if (route.length === 0) return null;

  let nearestIndex = 0;
  let nearestDistSq = Infinity;
  for (let i = 0; i < route.length; i++) {
    const dx = route[i].x - playerX;
    const dz = route[i].z - playerZ;
    const distSq = dx * dx + dz * dz;
    if (distSq < nearestDistSq) { nearestDistSq = distSq; nearestIndex = i; }
  }

  // Aim one waypoint further on, so the arrow leads rather than points at
  // the player's own feet. At the end of the route it clamps to the final
  // waypoint (the extraction room itself).
  const index = Math.min(nearestIndex + 1, route.length - 1);

  let remainingM = Math.hypot(route[index].x - playerX, route[index].z - playerZ);
  for (let i = index; i < route.length - 1; i++) {
    remainingM += Math.hypot(route[i + 1].x - route[i].x, route[i + 1].z - route[i].z);
  }

  return { x: route[index].x, z: route[index].z, remainingM, index };
}

export interface NearestGate { x: number; z: number; dist: number }

export interface CompassTarget {
  x: number;
  z: number;
  /** What to print next to the arrow: route-remaining for a route target, straight-line for a gate. */
  distanceM: number;
  source: 'gate' | 'route';
}

/**
 * Which of the two guidance sources the arrow should use this frame.
 *
 * Extracted from `dungeon-instance-bootstrap.ts`'s frame loop and made pure
 * specifically because the first version of this decision shipped inline and
 * untested, and was wrong: it preferred the straight-line bearing to the
 * nearest portcullis whenever one was within 20m, with no check that the
 * gate had anything to do with where the player was going. Measured over
 * 1000 generated layouts, in 4.0% of them some point on the *first half* of
 * the route came within 20m of an extraction portcullis (closest observed:
 * 8.0m), and in 0.4% that was already true at the spawn point — so the
 * arrow abandoned the breadcrumb and pointed through ten rooms of wall at a
 * gate the player could not reach, which is the exact failure the
 * breadcrumb exists to prevent.
 *
 * Two conditions hand off to the gate, and both are needed:
 *
 * - **Within `handoffM` of a gate.** Tight enough that a wall in between is
 *   implausible (`handoffM` is a small multiple of the interact distance).
 *   Works for *either* stairwell, including the one the fly-through route
 *   didn't pick — a player who walks to the other exit still gets a usable
 *   final bearing. At 6m and below this had zero false positives across the
 *   same 1000 layouts, against a measured 8.0m floor for how close the
 *   early route ever comes to a gate.
 * - **Route essentially complete.** The route's last waypoint is the
 *   extraction *room centre*, which sits a measured 6.3m from that room's
 *   portcullis — further than `handoffM`. Without this the arrow would flip
 *   back to the breadcrumb and point at the player's own feet once they
 *   stepped into the exit room. (The waypoint before it is the gate zone
 *   itself, 0.0m from the portcullis, so the doorway approach is already
 *   covered by the proximity test.)
 */
export function resolveCompassTarget(
  route: readonly RoutePoint[],
  nearestGate: NearestGate | null,
  playerX: number,
  playerZ: number,
  handoffM: number,
): CompassTarget | null {
  const guidance = nextRouteWaypoint(route, playerX, playerZ);
  const routeComplete = guidance !== null && guidance.remainingM <= handoffM;

  if (nearestGate && (nearestGate.dist <= handoffM || routeComplete)) {
    return { x: nearestGate.x, z: nearestGate.z, distanceM: nearestGate.dist, source: 'gate' };
  }
  if (guidance) return { x: guidance.x, z: guidance.z, distanceM: guidance.remainingM, source: 'route' };
  if (nearestGate) return { x: nearestGate.x, z: nearestGate.z, distanceM: nearestGate.dist, source: 'gate' };
  return null;
}

export class GoalCompass {
  private readonly root: HTMLDivElement;
  private readonly arrowEl: HTMLDivElement;
  private readonly labelEl: HTMLDivElement;

  constructor(container: HTMLElement) {
    this.root = document.createElement('div');
    this.root.dataset.tutorial = 'compass';
    /**
     * `top` is responsive because on a phone the whole top strip is contested.
     *
     * The objective plaque owns the top-left and at 390px reaches the centre,
     * which is what first pushed this down. The party frames are the other
     * claimant: they are right-anchored but `max-w-[46vw]` puts their left
     * edge at 199px on a 390px screen, and this arrow spans 153-237. With a
     * full party of four they run to 246px, so anything above that collides —
     * which it already did before the run-menu button took the corner and
     * pushed the frames down another 48px. Measured, not guessed: see
     * `hud-layout-harness` in the 2026-09-03 session.
     *
     * So on a phone this sits below a *full* party panel, and beside
     * everything on anything larger.
     *
     * One row, arrow beside the label (QA 2026-10-16). Stacked, the pair was
     * 54px tall and its label hung over the target frame's name line, which
     * starts at 3.5rem; side by side it ends at 2.75rem. On a touch device
     * `app.css` moves it again — under the objective plaque in portrait, top
     * centre in landscape — because the 16rem band is the interact prompt's.
     */
    this.root.dataset.hud = 'compass';
    this.root.className = 'fixed top-[16rem] sm:top-4 left-1/2 -translate-x-1/2 z-[1000] '
      + 'flex flex-row items-center gap-1.5 pointer-events-none hidden';

    // Restyled 2026-09-01: this was the text glyph '▲' — a character borrowed
    // from the font, which is exactly what it looked like. It is now the
    // painted waypoint arrow from the HUD glyph set, so the one element that
    // points you through the dungeon is drawn like the dungeon.
    this.arrowEl = document.createElement('div');
    this.arrowEl.className = 'w-7 h-7 leading-none';
    const arrowImg = document.createElement('img');
    arrowImg.src = glyphUrl('waypoint');
    arrowImg.alt = '';
    arrowImg.draggable = false;
    arrowImg.className = 'w-full h-full select-none drop-shadow-[0_2px_2px_rgba(0,0,0,0.85)]';
    // The glyph is drawn pointing down; a bearing of 0 must read as "ahead",
    // so the sprite is pre-rotated and `update` rotates from there.
    this.arrowEl.style.transform = 'rotate(180deg)';
    this.arrowEl.appendChild(arrowImg);

    this.labelEl = document.createElement('div');
    this.labelEl.className = `text-xs tracking-wide px-2 py-0.5 ${PLAQUE_SMALL} ${LABEL}`;

    this.root.append(this.arrowEl, this.labelEl);
    container.appendChild(this.root);
  }

  update(bearingRad: number, distanceM: number, label = t('Exit')): void {
    this.root.classList.remove('hidden');
    this.arrowEl.style.transform = `rotate(${180 - (bearingRad * 180) / Math.PI}deg)`;
    this.labelEl.textContent = t('{label} · {dist}m', { label, dist: Math.round(distanceM).toLocaleString(getIntlLocale()) });
  }

  hide(): void {
    this.root.classList.add('hidden');
  }

  dispose(): void {
    this.root.remove();
  }
}
